import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Drawer, Group, ScrollArea, Stack, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { tractInputSchema, type TractInputSchema } from '../../lib/schemas';
import { useCreateTract, useUpdateTract } from '../../queries/tracts';
import { useTractFormLayout } from '../../queries/tract-form-layout';
import { useProjectStore } from '../../project/project-store';
import {
  TRACT_FORM_FIELDS,
  TRACT_FORM_FIELD_NAMES,
  TRACT_DEFAULT_LAYOUT,
} from '../../lib/form-layout/tract-fields';
import { FormLayoutRenderer } from '../../lib/form-layout/FormLayoutRenderer';
import { ApiError } from '../../lib/api-error';
import type { Tract } from '../../types/api';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** If present -> edit mode. */
  tract?: Tract | null;
  onSaved?: (t: Tract) => void;
}

const EMPTY: TractInputSchema = {
  name: '',
  state: '',
  county: '',
  description: null,
  township: null,
  range: null,
  section: null,
  block: null,
  lot: null,
  abstract: null,
  survey: null,
  apn: null,
  gross_acres: null,
  notes: null,
};

function fromTract(t: Tract): TractInputSchema {
  return {
    name: t.name,
    state: t.state,
    county: t.county,
    description: t.description,
    township: t.township,
    range: t.range,
    section: t.section,
    block: t.block,
    lot: t.lot,
    abstract: t.abstract,
    survey: t.survey,
    apn: t.apn,
    gross_acres: t.gross_acres == null ? null : Number(t.gross_acres),
    notes: t.notes,
  };
}

function cleanNulls<T extends Record<string, unknown>>(obj: T): T {
  const out = { ...obj };
  for (const k of Object.keys(out)) {
    const v = out[k];
    if (typeof v === 'string' && v.trim() === '') {
      (out as Record<string, unknown>)[k] = null;
    }
  }
  return out;
}

export function TractFormDrawer({ opened, onClose, tract, onSaved }: Props) {
  const isEdit = !!tract;
  const createM = useCreateTract();
  const updateM = useUpdateTract(tract?.id ?? '');
  const projectId = useProjectStore((s) => s.projectId);
  const layoutQ = useTractFormLayout(projectId);
  const layout = layoutQ.data?.layout ?? TRACT_DEFAULT_LAYOUT;
  const [lastOpenKey, setLastOpenKey] = useState<string | null>(null);

  const form = useForm<TractInputSchema>({
    resolver: zodResolver(tractInputSchema),
    defaultValues: EMPTY,
  });

  const openKey = opened ? `${tract?.id ?? 'new'}-${tract?.updated_at ?? ''}` : null;
  if (openKey !== lastOpenKey) {
    setLastOpenKey(openKey);
    if (opened) form.reset(tract ? fromTract(tract) : EMPTY);
  }

  async function onSubmit(values: TractInputSchema) {
    try {
      const clean = cleanNulls(values);
      const res = isEdit
        ? await updateM.mutateAsync(clean)
        : await createM.mutateAsync(clean as Required<TractInputSchema>);
      notifications.show({
        title: isEdit ? 'Tract updated' : 'Tract created',
        message: res.tract.name,
        color: 'pine',
      });
      onSaved?.(res.tract);
      onClose();
    } catch (err) {
      notifications.show({
        title: isEdit ? 'Update failed' : 'Create failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      padding="md"
      title={<Title order={4}>{isEdit ? 'Edit tract' : 'New tract'}</Title>}
      scrollAreaComponent={ScrollArea.Autosize}
      overlayProps={{ backgroundOpacity: 0.15 }}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack gap="md">
          <FormLayoutRenderer
            layout={layout}
            control={form.control}
            register={form.register}
            registry={TRACT_FORM_FIELDS}
            allNames={TRACT_FORM_FIELD_NAMES}
            context={{}}
            isEdit={isEdit}
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {isEdit ? 'Save changes' : 'Create tract'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
